/**
 * AdminReleasesPage — what each deploy changed on the server, and a brief that tells users about it.
 *
 * deploy.sh records every deploy's commits (manage.py record_release). Here an admin reads the
 * changes, drafts a short brief (by hand or with AI), previews the email, sends a test to themselves
 * and then sends it to every user — one email per person, honouring their email settings.
 */
import React, { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import {
  Container, Paper, Typography, Box, Chip, CircularProgress, Alert, Button, Stack, Snackbar, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Divider, Accordion, AccordionSummary,
  AccordionDetails, ToggleButtonGroup, ToggleButton, List, ListItemButton, Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon, AutoAwesome as DraftIcon, Send as SendIcon, Visibility as PreviewIcon,
  Save as SaveIcon, Add as AddIcon, NewReleases as ReleaseIcon, MarkEmailRead as TestIcon,
  VisibilityOff as InternalIcon,
} from '@mui/icons-material';
import { axiosService, fetcher } from 'shared/helpers/axios';
import BackButton from 'components/common/buttons/BackButton';
import LabMarkdown from 'components/collab/lab/LabMarkdown';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '');
const STATUS = {
  draft: { label: 'Not sent', color: 'warning' },
  sent: { label: 'Sent', color: 'success' },
  internal: { label: 'Internal only', color: 'default' },
};
const QUIET_AREAS = ['Tests', 'Server and deployment', 'Admin pages', 'Other'];
const BASE = '/admin/email/releases/';

function ReleaseList({ releases, selectedId, onSelect, onNew }) {
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Deploys</Typography>
        <Tooltip title="A brief that is not tied to a deploy">
          <Button size="small" startIcon={<AddIcon />} onClick={onNew}>New brief</Button>
        </Tooltip>
      </Box>
      <Divider />
      {releases.length === 0 ? (
        <Typography sx={{ p: 2, fontSize: 13 }} color="text.secondary">
          No deploys recorded yet. The next <code>./deploy.sh update</code> records one, or run{' '}
          <code>./deploy.sh record-release</code> on the server.
        </Typography>
      ) : (
        <List dense disablePadding>
          {releases.map((r) => (
            <ListItemButton key={r.id} selected={r.id === selectedId} onClick={() => onSelect(r.id)}
              sx={{ display: 'block', py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center' }}>
                <Typography sx={{ fontWeight: 600, fontSize: 13.5 }}>{day(r.deployed_at)}</Typography>
                <Chip size="small" label={STATUS[r.status]?.label} color={STATUS[r.status]?.color}
                  variant={r.status === 'draft' ? 'outlined' : 'filled'} />
              </Box>
              <Typography sx={{ fontSize: 12.5, mt: 0.25 }} color="text.secondary" noWrap>
                {r.subject || (r.repo === 'manual' ? 'Manual brief' : `${r.commit_count} change(s) · ${r.repo} ${r.to_sha.slice(0, 7)}`)}
              </Typography>
            </ListItemButton>
          ))}
        </List>
      )}
    </Paper>
  );
}

function Changes({ changes }) {
  if (!changes?.length) {
    return <Typography color="text.secondary" sx={{ fontSize: 13.5 }}>No code changes recorded for this brief.</Typography>;
  }
  return changes.map(({ area, commits }) => (
    <Accordion key={area} disableGutters defaultExpanded={!QUIET_AREAS.includes(area)} variant="outlined"
      sx={{ '&:before': { display: 'none' }, mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{area}</Typography>
        <Chip size="small" label={commits.length} sx={{ ml: 1 }} />
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Stack spacing={1}>
          {commits.map((c) => (
            <Box key={c.sha}>
              <Typography sx={{ fontSize: 13.5 }}>{c.subject}</Typography>
              <Typography sx={{ fontSize: 12 }} color="text.secondary">
                {c.sha.slice(0, 7)} · {c.author} · {fmt(c.date)} · {c.files.length} file(s)
              </Typography>
            </Box>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  ));
}

export default function AdminReleasesPage() {
  const { data: list, mutate: mutateList, isLoading } = useSWR(BASE, fetcher);
  const releases = useMemo(() => list?.results || [], [list]);
  const [selectedId, setSelectedId] = useState(null);
  const { data: release, mutate } = useSWR(selectedId ? `${BASE}${selectedId}/` : null, fetcher);
  const [subject, setSubject] = useState('');
  const [summary, setSummary] = useState('');
  const [view, setView] = useState('write');
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [confirm, setConfirm] = useState(null);   // { emails, skipped }

  useEffect(() => {
    if (!selectedId && releases.length) setSelectedId(releases[0].id);
  }, [releases, selectedId]);
  useEffect(() => {          // load the brief once per release (not on every revalidation)
    if (release) { setSubject(release.subject || ''); setSummary(release.summary || ''); }
  }, [release?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const sent = release?.status === 'sent';
  const dirty = release && (subject !== (release.subject || '') || summary !== (release.summary || ''));
  const ready = subject.trim() && summary.trim();
  const refresh = (r) => { mutate(r, false); mutateList(); };
  const fail = (e, fallback) => setToast(e.response?.data?.error
    || Object.values(e.response?.data?.errors || {})[0] || fallback);

  const save = async (extra = {}) => {
    const r = (await axiosService.patch(`${BASE}${selectedId}/`, { subject, summary, ...extra })).data;
    refresh(r);
    return r;
  };
  const run = (name, fn) => async () => {
    setBusy(name);
    try { await fn(); } finally { setBusy(''); }
  };

  const draft = run('draft', async () => {
    try {
      if (dirty && !window.confirm('Replace your unsaved brief with an AI draft?')) return;
      const r = (await axiosService.post(`${BASE}${selectedId}/draft/`)).data;
      setSubject(r.subject || ''); setSummary(r.summary || ''); refresh(r);
      setToast(r.user_visible === false ? 'Nothing in these changes is visible to users, so there is no brief.' : 'Draft ready. Read it before sending.');
    } catch (e) { fail(e, 'Could not draft the brief.'); }
  });
  const saveBrief = run('save', async () => {
    try { await save(); setToast('Saved.'); } catch (e) { fail(e, 'Could not save.'); }
  });
  const preview = run('preview', async () => {
    try {
      if (dirty) await save();
      setPreviewHtml((await axiosService.post(`${BASE}${selectedId}/preview/`, {}, { responseType: 'text' })).data);
    } catch (e) { fail(e, 'Could not render the email.'); }
  });
  const sendTest = run('test', async () => {
    try {
      if (dirty) await save();
      const r = (await axiosService.post(`${BASE}${selectedId}/send/`, { mode: 'test' })).data;
      setToast(r.sent ? `Test sent to ${r.to}.` : 'The test email could not be sent.');
    } catch (e) { fail(e, 'Could not send the test.'); }
  });
  const reviewSend = run('review', async () => {
    try {
      if (dirty) await save();
      setConfirm((await axiosService.get(`${BASE}${selectedId}/send/`)).data);
    } catch (e) { fail(e, 'Could not count the recipients.'); }
  });
  const sendAll = run('send', async () => {
    try {
      const r = (await axiosService.post(`${BASE}${selectedId}/send/`, { mode: 'all', expected: confirm.emails })).data;
      setConfirm(null);
      setToast(`Sent to ${r.sent} user(s)${r.failed ? `; ${r.failed} failed` : ''}.`);
      mutate(); mutateList();
    } catch (e) { setConfirm(null); fail(e, 'Could not send.'); }
  });
  const toggleInternal = run('internal', async () => {
    try { await save({ status: release.status === 'internal' ? 'draft' : 'internal' }); } catch (e) { fail(e, 'Could not update.'); }
  });
  const newBrief = async () => {
    try {
      const r = (await axiosService.post(BASE, { subject: '', summary: '' })).data;
      await mutateList();
      setSelectedId(r.id);
    } catch (e) { fail(e, 'Could not create a brief.'); }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
        <BackButton />
        <ReleaseIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Releases</Typography>
      </Box>
      <Typography color="text.secondary" sx={{ mb: 3, fontSize: 14 }}>
        What each deploy changed on the server, and a short brief to tell users about new features.
      </Typography>

      {isLoading ? <CircularProgress /> : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '300px 1fr' }, gap: 3, alignItems: 'start' }}>
          <ReleaseList releases={releases} selectedId={selectedId} onSelect={setSelectedId} onNew={newBrief} />

          {release ? (
            <Stack spacing={3}>
              <Paper variant="outlined" sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 16, mr: 1 }}>
                    {release.repo === 'manual' ? 'Brief' : `Deployed ${fmt(release.deployed_at)}`}
                  </Typography>
                  <Chip size="small" label={STATUS[release.status]?.label} color={STATUS[release.status]?.color} />
                  {release.repo !== 'manual' && (
                    <Chip size="small" variant="outlined"
                      label={`${release.repo} ${release.from_sha ? `${release.from_sha.slice(0, 7)} → ` : ''}${release.to_sha.slice(0, 7)}`} />
                  )}
                </Box>
                {sent && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Sent to {release.sent_count} user(s) on {fmt(release.sent_at)}{release.sent_by ? ` by ${release.sent_by}` : ''}.
                  </Alert>
                )}
                <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1.5 }}>
                  What changed on the server ({release.commit_count} change{release.commit_count === 1 ? '' : 's'})
                </Typography>
                <Changes changes={release.changes} />
              </Paper>

              <Paper variant="outlined" sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Brief for users</Typography>
                  <Button size="small" startIcon={busy === 'draft' ? <CircularProgress size={14} /> : <DraftIcon />}
                    disabled={sent || !release.commit_count || !!busy} onClick={draft}>
                    Draft with {release.drafted_by || 'AI'}
                  </Button>
                </Box>
                <TextField label="Email subject" fullWidth size="small" value={subject} disabled={sent}
                  onChange={(e) => setSubject(e.target.value)} inputProps={{ maxLength: 200 }} sx={{ mb: 2 }} />
                <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)} sx={{ mb: 1 }}>
                  <ToggleButton value="write">Write</ToggleButton>
                  <ToggleButton value="read">Preview</ToggleButton>
                </ToggleButtonGroup>
                {view === 'write' ? (
                  <TextField multiline minRows={10} fullWidth value={summary} disabled={sent}
                    onChange={(e) => setSummary(e.target.value)}
                    placeholder={'Markdown: **bold**, *italic*, [link](https://…), lists with "- ".\n'
                      + 'Every email starts with "Dear <first name>," so start with the first paragraph.'} />
                ) : (
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, minHeight: 200 }}>
                    {summary.trim() ? <LabMarkdown>{summary}</LabMarkdown>
                      : <Typography color="text.secondary">Nothing written yet.</Typography>}
                  </Box>
                )}

                {!sent && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                    <Button variant="outlined" startIcon={<SaveIcon />} disabled={!dirty || !!busy} onClick={saveBrief}>Save</Button>
                    <Button variant="outlined" startIcon={<PreviewIcon />} disabled={!ready || !!busy} onClick={preview}>Preview email</Button>
                    <Button variant="outlined" startIcon={<TestIcon />} disabled={!ready || !!busy} onClick={sendTest}>Send test to me</Button>
                    <Button variant="contained" startIcon={<SendIcon />}
                      disabled={!ready || !!busy || release.status !== 'draft'} onClick={reviewSend}>
                      Send to all users
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    <Tooltip title={release.status === 'internal'
                      ? 'Allow sending this brief again' : 'Nothing here for users: keep it off the email list'}>
                      <Button color="inherit" startIcon={<InternalIcon />} disabled={!!busy} onClick={toggleInternal}>
                        {release.status === 'internal' ? 'Not internal' : 'Internal only'}
                      </Button>
                    </Tooltip>
                  </Box>
                )}
              </Paper>
            </Stack>
          ) : selectedId && <CircularProgress />}
        </Box>
      )}

      <Dialog open={!!previewHtml} onClose={() => setPreviewHtml(null)} maxWidth="md" fullWidth>
        <DialogTitle>Email preview</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box component="iframe" title="Email preview" srcDoc={previewHtml || ''} sandbox=""
            sx={{ width: '100%', height: '70vh', border: 0, display: 'block' }} />
        </DialogContent>
        <DialogActions><Button onClick={() => setPreviewHtml(null)}>Close</Button></DialogActions>
      </Dialog>

      <Dialog open={!!confirm} onClose={() => busy !== 'send' && setConfirm(null)}>
        <DialogTitle>Send this brief to all users?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            <strong>{confirm?.emails}</strong> people get one email each: “{subject}”.
          </Typography>
          {!!confirm?.skipped && (
            <Typography color="text.secondary" sx={{ fontSize: 13.5 }}>
              {confirm.skipped} people are skipped because they turned off email notifications.
            </Typography>
          )}
          <Typography color="text.secondary" sx={{ fontSize: 13.5, mt: 1 }}>
            A brief can only be sent once.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy === 'send'}>Cancel</Button>
          <Button variant="contained" onClick={sendAll} disabled={busy === 'send' || !confirm?.emails}
            startIcon={busy === 'send' ? <CircularProgress size={14} /> : <SendIcon />}>
            Send {confirm?.emails} email(s)
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={6000} onClose={() => setToast(null)} message={toast} />
    </Container>
  );
}
